import { useState, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Upload,
  Download,
  FileText,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { questionImportExportApi, questionBanksApi } from '@/api/endpoints';
import { QuestionBank } from '@/types';
import { toast } from '@/utils/toast';

export const QuestionImportExportPage = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importFormat, setImportFormat] = useState<'csv' | 'json'>('csv');
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [importBankId, setImportBankId] = useState('');
  const [exportBankId, setExportBankId] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<{ imported: number; failed: number; errors: string[] } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const { data: banksRes } = useQuery({
    queryKey: ['question-banks-ie'],
    queryFn: () => questionBanksApi.list({ page: 1, per_page: 200 }),
    staleTime: 30000,
  });

  const banks: QuestionBank[] = Array.isArray(banksRes?.data?.data?.data)
    ? banksRes.data.data.data
    : Array.isArray(banksRes?.data?.data)
      ? banksRes.data.data
      : [];

  const importMutation = useMutation({
    mutationFn: (formData: FormData) => questionImportExportApi.importQuestions(formData),
    onSuccess: (res) => {
      const data = res?.data?.data as any;
      setImportResult({
        imported: data?.imported || 0,
        failed: data?.failed || 0,
        errors: data?.errors || [],
      });
      toast.success(`${data?.imported || 0} questions imported`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Import failed'),
  });

  const handleImport = () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    // Auto-detect format from file extension
    const detectedFormat = selectedFile.name.endsWith('.json') ? 'json' : 'csv';
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('format', detectedFormat);
    if (importBankId) formData.append('question_bank_id', importBankId);
    importMutation.mutate(formData);
  };

  const handleDownloadTemplate = async (format: 'csv' | 'json') => {
    try {
      const response = await questionImportExportApi.getImportTemplate(format);
      const blob = new Blob([response.data as any], { type: format === 'csv' ? 'text/csv' : 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `question-template.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleExport = async () => {
    try {
      const response = await questionImportExportApi.exportQuestions({
        question_bank_id: exportBankId || undefined,
        format: exportFormat,
      });
      const blob = new Blob([response.data as any], { type: exportFormat === 'csv' ? 'text/csv' : 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `questions-export.${exportFormat}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Questions exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white/90">Question Import / Export</h1>
        <p className="text-sm text-white/50 mt-1">Import questions from files or export your question banks</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Import Section */}
        <div className="card-dark p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
              <Upload className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white/90">Import Questions</h2>
              <p className="text-xs text-white/50">Upload CSV or JSON files</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-white/70 mb-1">Format</label>
              <select value={importFormat} onChange={(e) => setImportFormat(e.target.value as 'csv' | 'json')} className="input-dark w-full">
                <option value="csv">CSV</option>
                <option value="json">JSON</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-white/70 mb-1">Question Bank (optional)</label>
              <select value={importBankId} onChange={(e) => setImportBankId(e.target.value)} className="input-dark w-full">
                <option value="">-- Select bank --</option>
                {banks.map((bank) => (
                  <option key={bank.id} value={bank.id}>{bank.name}</option>
                ))}
              </select>
            </div>

            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                dragOver ? 'border-teal-500/50 bg-teal-500/10' : 'border-white/[0.08] hover:border-white/[0.16]'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.json"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              {selectedFile ? (
                <div className="flex items-center justify-center space-x-2">
                  <FileText className="w-5 h-5 text-teal-400" />
                  <span className="text-sm text-white/90">{selectedFile.name}</span>
                  <button onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }} className="text-red-400 hover:text-red-300">
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-white/30 mx-auto mb-2" />
                  <p className="text-sm text-white/50">Drag & drop or click to select file</p>
                  <p className="text-xs text-white/30 mt-1">Supports CSV and JSON</p>
                </>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <button onClick={handleImport} disabled={!selectedFile || importMutation.isPending} className="btn-primary px-4 py-2 rounded-lg text-sm flex-1 disabled:opacity-50">
                {importMutation.isPending ? 'Importing...' : 'Import'}
              </button>
              <button onClick={() => handleDownloadTemplate(importFormat)} className="btn-outline px-4 py-2 rounded-lg text-sm flex items-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Template</span>
              </button>
            </div>

            {/* Import results */}
            {importResult && (
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4 space-y-2">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-white/80">{importResult.imported} imported</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-sm text-white/80">{importResult.failed} failed</span>
                  </div>
                </div>
                {importResult.errors.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {importResult.errors.slice(0, 5).map((err, i) => (
                      <div key={i} className="flex items-start space-x-2">
                        <AlertCircle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-white/60">{err}</span>
                      </div>
                    ))}
                    {importResult.errors.length > 5 && (
                      <p className="text-xs text-white/40">...and {importResult.errors.length - 5} more errors</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Export Section */}
        <div className="card-dark p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Download className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white/90">Export Questions</h2>
              <p className="text-xs text-white/50">Download question banks as files</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-white/70 mb-1">Question Bank</label>
              <select value={exportBankId} onChange={(e) => setExportBankId(e.target.value)} className="input-dark w-full">
                <option value="">All Question Banks</option>
                {banks.map((bank) => (
                  <option key={bank.id} value={bank.id}>{bank.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-white/70 mb-1">Format</label>
              <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json')} className="input-dark w-full">
                <option value="csv">CSV</option>
                <option value="json">JSON</option>
              </select>
            </div>

            <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4 flex items-center space-x-3">
              <FileSpreadsheet className="w-8 h-8 text-white/20" />
              <div>
                <p className="text-sm text-white/70">Ready to export</p>
                <p className="text-xs text-white/40">Questions will be downloaded as {exportFormat.toUpperCase()}</p>
              </div>
            </div>

            <button onClick={handleExport} className="btn-primary px-4 py-2 rounded-lg text-sm w-full flex items-center justify-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Export Questions</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
